import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, humanBytes, formatApiError } from "../lib/api";
import {
    Download,
    CheckCircle2,
    Sparkles,
    Loader2,
    X,
    CalendarClock,
    Image as ImageIcon,
    Video as VideoIcon,
    FileType2,
    FileText,
    FileQuestion,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Toaster, toast } from "sonner";
import FileComments from "../components/FileComments";

const KIND_ICONS = {
    image: ImageIcon,
    video: VideoIcon,
    pdf: FileType2,
    text: FileText,
    other: FileQuestion,
};

const pub = axios.create({ baseURL: API });

export default function SharedFilePage() {
    const { token } = useParams();
    const [file, setFile] = useState(null);
    const [error, setError] = useState("");
    const [name, setName] = useState(() => localStorage.getItem("sf_visitor_name") || "");
    const [busyAction, setBusyAction] = useState(null);
    const videoRef = useRef(null);
    const nameRef = useRef(null);

    useEffect(() => {
        pub.get(`/fshare/${token}`)
            .then((r) => setFile(r.data))
            .catch((e) => setError(formatApiError(e)));
    }, [token]);

    const sendFeedback = async (action) => {
        if (!name.trim()) {
            toast.error("Vul eerst je naam in om feedback te geven.");
            nameRef.current?.focus();
            return;
        }
        localStorage.setItem("sf_visitor_name", name.trim());
        setBusyAction(action);
        try {
            await pub.post(`/fshare/${token}/feedback`, {
                author_name: name.trim(),
                action,
            });
            setFile((p) => ({
                ...p,
                [action === "favorite" ? "favorite" : "approved"]: true,
            }));
            toast.success(
                action === "favorite"
                    ? "Aangeduid als favoriet — bedankt!"
                    : "Bestand goedgekeurd — bedankt!"
            );
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyAction(null);
        }
    };

    if (error) return <ErrorScreen message={error} />;
    if (!file) {
        return (
            <div className="min-h-screen bg-[#f4f4f5] flex items-center justify-center text-zinc-500">
                <Loader2 className="w-6 h-6 animate-spin" />
            </div>
        );
    }

    const Icon = KIND_ICONS[file.kind] || FileQuestion;
    const { customer, share } = file;

    return (
        <div className="min-h-screen bg-[#f4f4f5]">
            {/* Header */}
            <header className="bg-[#09090b] text-white sticky top-0 z-20 border-b border-white/10">
                <div className="px-4 md:px-10 py-3.5 max-w-[1500px] mx-auto flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        {customer?.logo_url ? (
                            <img
                                src={customer.logo_url}
                                alt={customer.name}
                                className="w-9 h-9 rounded-sm bg-white object-contain border border-white/20"
                            />
                        ) : (
                            <div className="w-9 h-9 bg-[#0044FF] text-white rounded-sm flex items-center justify-center font-display font-black">
                                {(customer?.name || "?").slice(0, 1).toUpperCase()}
                            </div>
                        )}
                        <div className="min-w-0">
                            <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-400">
                                Gedeeld bestand
                            </div>
                            <div className="font-display text-base md:text-lg font-bold leading-none tracking-tight truncate">
                                {customer?.name}
                            </div>
                        </div>
                    </div>
                    <div className="text-right shrink-0">
                        <div className="font-display font-black tracking-tighter text-lg leading-none">
                            Social Factory
                        </div>
                        <div className="text-[9px] uppercase tracking-[0.3em] text-[#6b8fff]">
                            Portal AI
                        </div>
                    </div>
                </div>
            </header>

            <main
                className="px-4 md:px-10 py-6 md:py-10 max-w-[1500px] mx-auto"
                data-testid="fshare-page"
            >
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 md:gap-8">
                    {/* Preview */}
                    <div className="lg:col-span-3">
                        {file.kind === "video" && file.preview_url ? (
                            <video
                                ref={videoRef}
                                src={file.preview_url}
                                controls
                                playsInline
                                className="w-full max-h-[80vh] bg-black rounded-md border border-zinc-200 shadow-sm"
                                data-testid="fshare-video"
                            />
                        ) : (
                            <div className="bg-white border border-zinc-200 rounded-md shadow-sm flex items-center justify-center overflow-hidden min-h-[40vh] max-h-[80vh] h-[60vh] md:h-[75vh]">
                                {file.kind === "image" && file.preview_url ? (
                                    <img
                                        src={file.preview_url}
                                        alt={file.original_name}
                                        className="w-full h-full object-contain"
                                        data-testid="fshare-image"
                                    />
                                ) : file.kind === "pdf" && file.preview_url ? (
                                    <iframe
                                        src={file.preview_url}
                                        title={file.original_name}
                                        className="w-full h-full bg-white"
                                        data-testid="fshare-pdf"
                                    />
                                ) : (
                                    <div className="flex flex-col items-center gap-3 text-zinc-400">
                                        <Icon className="w-16 h-16" />
                                        <span className="text-xs font-mono uppercase">{file.kind}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Info panel */}
                    <div className="lg:col-span-2 flex flex-col gap-5">
                        <div className="bg-white border border-zinc-200 rounded-md p-5 md:p-6">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                                Bestand
                            </div>
                            <h1 className="font-display text-2xl md:text-3xl tracking-tight font-black leading-tight break-all">
                                {file.original_name}
                            </h1>
                            <div className="text-xs font-mono text-zinc-500 mt-2">
                                {file.mime_type} · {humanBytes(file.size_bytes)}
                            </div>
                            {share?.expires_at && (
                                <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-zinc-100 border border-zinc-200 text-[11px] font-mono text-zinc-600">
                                    <CalendarClock className="w-3 h-3" />
                                    Link geldig tot{" "}
                                    {new Date(share.expires_at).toLocaleDateString("nl-BE", {
                                        day: "numeric",
                                        month: "long",
                                        year: "numeric",
                                    })}
                                </div>
                            )}

                            {share?.allow_download && file.download_url && (
                                <a
                                    href={file.download_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0044FF] hover:bg-[#0033CC] text-white rounded-md text-sm font-medium transition-colors"
                                    data-testid="fshare-download"
                                >
                                    <Download className="w-4 h-4" />
                                    Download bestand
                                </a>
                            )}
                        </div>

                        {/* Feedback */}
                        <div className="bg-white border border-zinc-200 rounded-md p-5 md:p-6">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
                                Jouw feedback
                            </div>
                            <Input
                                ref={nameRef}
                                placeholder="Je naam *"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="mb-3"
                                data-testid="fshare-name-input"
                            />
                            <div className="grid grid-cols-1 gap-2">
                                <button
                                    onClick={() => sendFeedback("approve")}
                                    disabled={busyAction !== null || file.approved}
                                    className={`inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md text-sm font-medium border transition-all ${
                                        file.approved
                                            ? "bg-emerald-50 border-emerald-200 text-emerald-700 cursor-default"
                                            : "bg-white border-zinc-200 text-zinc-700 hover:border-emerald-400 hover:bg-emerald-50"
                                    }`}
                                    data-testid="fshare-approve-btn"
                                >
                                    {busyAction === "approve" ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <CheckCircle2
                                            className={`w-4 h-4 ${
                                                file.approved ? "text-emerald-600" : ""
                                            }`}
                                        />
                                    )}
                                    {file.approved ? "Goedgekeurd" : "Goedkeuren"}
                                </button>
                            </div>
                        </div>

                        {/* AI description */}
                        {(file.ai_description || file.ai_tags?.length > 0) && (
                            <div className="bg-white border border-zinc-200 rounded-md p-5 md:p-6">
                                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
                                    <Sparkles className="w-3 h-3" /> AI Beschrijving
                                </div>
                                {file.ai_description && (
                                    <p className="text-sm text-zinc-800 leading-relaxed whitespace-pre-wrap">
                                        {file.ai_description}
                                    </p>
                                )}
                                {file.ai_tags?.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-4">
                                        {file.ai_tags.map((t) => (
                                            <span
                                                key={t}
                                                className="inline-flex items-center px-2 py-1 rounded-sm bg-zinc-100 border border-zinc-200 text-xs font-mono text-zinc-700"
                                            >
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Comments */}
                        <div className="bg-white border border-zinc-200 rounded-md p-5 md:p-6">
                            <FileComments
                                fileId={file.id}
                                mode="fshare"
                                shareToken={token}
                                videoRef={file.kind === "video" ? videoRef : undefined}
                            />
                        </div>
                    </div>
                </div>

                <footer className="mt-16 pt-8 border-t border-zinc-200 text-xs text-zinc-500 text-center">
                    Gedeeld via{" "}
                    <a
                        href="https://portal.social-factory.be"
                        className="text-[#0044FF] hover:underline"
                    >
                        Social Factory Portal AI
                    </a>
                </footer>
            </main>

            <Toaster richColors position="top-right" />
        </div>
    );
}

function ErrorScreen({ message }) {
    return (
        <div className="min-h-screen bg-[#f4f4f5] flex items-center justify-center px-4">
            <div className="bg-white border border-zinc-200 rounded-md p-8 max-w-md text-center" data-testid="fshare-error">
                <div className="w-12 h-12 mx-auto rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-4">
                    <X className="w-6 h-6" />
                </div>
                <h1 className="font-display text-2xl tracking-tight font-bold mb-2">
                    Link niet beschikbaar
                </h1>
                <p className="text-sm text-zinc-600 mb-4">{message}</p>
                <a
                    href="https://portal.social-factory.be"
                    className="inline-flex items-center gap-1.5 text-sm text-[#0044FF] hover:underline"
                >
                    Naar Social Factory Portal
                </a>
            </div>
        </div>
    );
}
